import React, { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

// Shared pill-styled button/container primitives used by both viewer engines
// (ReactPdfViewer, EmbedPdfViewer) and by feature toolbars built on top of them.

interface ToolbarButtonProps {
    icon: IconDefinition;
    title: string;
    onClick?: () => void;
    disabled?: boolean;
}

function ToolbarButton({ icon, title, onClick, disabled }: ToolbarButtonProps) {
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            onClick={onClick}
            disabled={disabled}
            className="flex h-9 w-9 shrink-0 appearance-none items-center justify-center rounded-full! border-0 bg-transparent p-0 leading-none text-[currentColor] transition-colors enabled:hover:bg-black/10 disabled:opacity-40"
        >
            <FontAwesomeIcon icon={icon} />
        </button>
    );
}

interface ToolbarPillProps {
    variant?: "default" | "accent";
    children: ReactNode;
}

function ToolbarPill({ variant = "default", children }: ToolbarPillProps) {
    const variantClasses =
        variant === "accent"
            ? "bg-accent text-white border-transparent"
            : "bg-surface/95 text-ink border-border";

    return (
        <div
            className={[
                "flex items-center gap-1 rounded-full border p-1 shadow-md backdrop-blur",
                variantClasses,
            ].join(" ")}
        >
            {children}
        </div>
    );
}

interface ToolbarPillButtonProps extends ToolbarButtonProps {
    variant?: "default" | "accent";
    // For toggle buttons: exposes the expanded/collapsed state to assistive tech.
    ariaExpanded?: boolean;
}

function ToolbarPillButton({
    icon,
    title,
    onClick,
    disabled,
    variant = "default",
    ariaExpanded,
}: ToolbarPillButtonProps) {
    const variantClasses =
        variant === "accent"
            ? "bg-accent text-white border-transparent enabled:hover:bg-accent-hover"
            : "bg-surface/95 text-ink border-border enabled:hover:bg-surface-3";

    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            aria-expanded={ariaExpanded}
            onClick={onClick}
            disabled={disabled}
            className={[
                "flex h-[46px] w-[46px] shrink-0 appearance-none items-center justify-center rounded-full! border p-0 leading-none shadow-md backdrop-blur transition-colors disabled:opacity-40 cursor-pointer",
                variantClasses,
            ].join(" ")}
        >
            <FontAwesomeIcon icon={icon} />
        </button>
    );
}

export { ToolbarButton, ToolbarPill, ToolbarPillButton };
